import { Colors } from 'styles';
import { RightDrawerWrapper } from '../RightDrawer/RightDrawerWrapper';
import styled from 'styled-components';
import Text from 'common/ui/Text';
import {
  isSingleBracketSelected,
  getActiveBracket,
  PICKLIST_PLUGIN_ID,
  INLINE_BOOKMARK_PLUGIN_ID,
} from 'domains/reporter/RichTextEditor';
import { getDefaultFieldName } from 'domains/reporter/RichTextEditor/utils/fieldNaming';
import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import Input from 'common/ui/Input';
import { Stack } from 'common/ui/Layout';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { FieldBuilderPanePicklist, emptyParagraph } from './FieldBuilderPane.Picklist';
import {
  NodeEntry,
  Transforms,
  useSlateSelector,
  useSlateStatic,
  useSlateSelection,
} from 'domains/reporter/RichTextEditor/core';
import type { FieldBuilderFormErrors, FieldBuilderForm } from './types';
import { logger } from 'modules/logger';
import { isSelectionContainedInNode } from '../../RichTextEditor/utils/isSelectionContainedInNode';
import { FIELD_BUILDER_PANE_WIDTH } from './constants';

const HeaderTitle = styled.h2`
  all: unset;
  padding-left: 12px;
`;
const Section = styled.div`
  padding: 24px;
  color: ${Colors.gray9};
`;

const getActiveFieldBracket = (
  editor: ReturnType<typeof useSlateStatic>
): NodeEntry<any> | null => {
  const selection = editor?.selection;

  if (editor == null || selection == null) {
    return null;
  }

  if (!isSingleBracketSelected(editor)) {
    return null;
  }

  const bracket = getActiveBracket(editor, selection);

  if (bracket == null) {
    return null;
  }

  if (!isSelectionContainedInNode(editor, bracket[1], selection)) {
    return null;
  }

  return bracket;
};

export const useIsFieldSelectionActive = (): boolean => {
  const editor = useSlateStatic();
  const isActive = useSlateSelector((editor) => getActiveFieldBracket(editor) != null);
  const [didSyncAfterLayout, setDidSyncAfterLayout] = useState(false);

  useEffect(() => setDidSyncAfterLayout(true), []);

  // The editor applies its default selection in a sibling layout effect. Re-read the live editor
  // after mount so this hook does not depend on an unrelated rerender to observe that selection.
  return didSyncAfterLayout ? getActiveFieldBracket(editor) != null : isActive;
};

export const FieldBuilderPaneContent = (): React.ReactElement => {
  const editor = useSlateStatic();
  const selection = useSlateSelection();
  const [form, setForm] = useState<FieldBuilderForm>({
    fieldName: '',
    isRequiredField: false,
    isPicklist: false,
    picklistForm: {
      name: '',
      value: [...emptyParagraph],
      options: [],
    },
  });
  const [formErrors, setFormErrors] = useState<FieldBuilderFormErrors>({
    fieldName: '',
    isRequiredField: '',
    isPicklist: '',
    picklistForm: {
      name: '',
      value: '',
      options: '',
    },
  });
  const [singleBracketSelected, setSingleBracketSelected] = useState(
    getActiveFieldBracket(editor) != null
  );
  const [activeBracket, setActiveBracket] = useState<NodeEntry<any> | null>(null);
  const [didSyncAfterMount, setDidSyncAfterMount] = useState(false);

  const setFieldNameToDefault = useCallback(() => {
    if (editor === null) return;
    setForm((form) => ({
      ...form,
      fieldName: getDefaultFieldName(editor, activeBracket),
    }));
  }, [activeBracket, editor]);

  /**
   *  Handles field pane initialization on every new selection. Populates the default field name and checks if
   *  the field is a picklist or inlineBookmark. Note there are 2 RichTextEditors - one for the template/report
   *  and one for picklist. The editor comes from useSlate via SlateContextProvider and refers to the
   *  template/report editor.
   */
  const syncActiveField = useCallback(() => {
    const bracket = getActiveFieldBracket(editor);

    if (bracket == null) {
      setActiveBracket(null);
      setSingleBracketSelected(false);
      return;
    }

    setSingleBracketSelected(true);
    setActiveBracket(bracket);
    setForm((form) => ({
      ...form,
      isPicklist: bracket[0].type === PICKLIST_PLUGIN_ID,
    }));
  }, [editor]);

  useLayoutEffect(syncActiveField, [selection, syncActiveField, didSyncAfterMount]);

  useEffect(() => {
    const frame = requestAnimationFrame(() => setDidSyncAfterMount(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    if (activeBracket) {
      setFieldNameToDefault();
      setForm((form) => ({
        ...form,
        isRequiredField: Boolean(activeBracket[0].required),
      }));
    }
  }, [activeBracket, setFieldNameToDefault]);

  const handleTogglePicklistInlineBookmark = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (editor == null || activeBracket === null) return;

    const isPicklist = e.target.checked;
    const [, activeBracketPath] = activeBracket;

    setForm((form) => ({ ...form, isPicklist }));

    Transforms.setNodes(
      editor,
      {
        type: isPicklist ? PICKLIST_PLUGIN_ID : INLINE_BOOKMARK_PLUGIN_ID,
        // Set picklistID back to undefined when toggling from picklist to InlineBookmark
        picklistID: !isPicklist && undefined,
      },
      { at: activeBracketPath }
    );

    logger.info('[FieldBuilderPane] Picklist checkbox toggled in pane', {
      value: e.target.checked,
      name: form?.fieldName ?? '',
    });
  };

  /**
   * If fieldName is empty set it back to the default field name to prevent error on save, otherwise update the field name.
   */
  const handleFieldNameOnBlur = useCallback(() => {
    if (editor == null || activeBracket == null || selection == null) return;

    try {
      const { fieldName } = form;

      if (fieldName.trim().length === 0) {
        setFieldNameToDefault();
      } else {
        Transforms.setNodes(editor, { name: fieldName }, { at: activeBracket[1] });
      }

      setFormErrors((error) => ({ ...error, fieldName: '' }));

      logger.info('[FieldBuilderPane] Field name saved in pane', {
        name: form?.fieldName ?? '',
      });
    } catch (e) {
      setFormErrors((error) => ({ ...error, fieldName: 'Error saving field name.' }));
      logger.error('[FieldBuilderPane] Error saving field name', e);
    }
  }, [activeBracket, editor, form, selection, setFieldNameToDefault]);

  const handleFieldNameInputKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter' || e.nativeEvent.isComposing) {
      return;
    }

    // `Enter` blurs to trigger handleFieldNameOnBlur
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.blur();
  }, []);

  return (
    <>
      <Section>
        <HeaderTitle>Field</HeaderTitle>
        <div>
          {singleBracketSelected ? (
            <div role="form" aria-label="Field builder">
              <Stack vertical stretchY>
                <Text
                  data-testid="field-name-label"
                  css={{ paddingLeft: '12px', marginTop: '12px' }}
                  variant="caption"
                >
                  Field name
                </Text>
                <Input
                  data-testid="field-name-input"
                  name="fieldName"
                  autoComplete="off"
                  css={{
                    marginTop: '4px',
                    backgroundColor: Colors.gray2,
                    borderColor: formErrors.fieldName !== '' ? Colors.red6 : Colors.gray8,
                  }}
                  value={form.fieldName}
                  onChange={(e) => {
                    setForm((form) => ({ ...form, fieldName: e.target.value }));
                  }}
                  onKeyDown={handleFieldNameInputKeyDown}
                  onBlur={handleFieldNameOnBlur}
                />
                {formErrors.fieldName !== '' && (
                  <Text
                    variant="caption"
                    css={{ marginTop: '4px', marginLeft: '12px', color: Colors.red6 }}
                  >
                    {formErrors.fieldName}&nbsp;
                  </Text>
                )}
                <FormControlLabel
                  data-testid="required-checkbox"
                  css={{ marginTop: '4px' }}
                  value={form.isRequiredField}
                  checked={form.isRequiredField}
                  control={
                    <Checkbox
                      color="primary"
                      onChange={(e) => {
                        const isRequiredField = e.target.checked;
                        setForm((form) => ({ ...form, isRequiredField }));
                        if (editor && editor.selection && activeBracket) {
                          const [, path] = activeBracket;
                          Transforms.setNodes(editor, { required: isRequiredField }, { at: path });
                        }
                        logger.info('[FieldBuilderPane] Required checkbox toggled in pane', {
                          value: e.target.checked,
                          name: form?.fieldName ?? '',
                        });
                      }}
                    />
                  }
                  label={<span css="font-size: 14px;">Required field</span>}
                />

                <FormControlLabel
                  data-testid="picklist-checkbox"
                  value={form.isPicklist}
                  checked={form.isPicklist}
                  control={
                    <Checkbox color="primary" onChange={handleTogglePicklistInlineBookmark} />
                  }
                  label={<span css="font-size: 14px;">Picklist</span>}
                />
              </Stack>
            </div>
          ) : (
            <Text css="text-align: center" gutterTop="large" display="block" color="secondary-alt">
              No field selected
            </Text>
          )}
        </div>
      </Section>
      {form.isPicklist && singleBracketSelected && (
        <FieldBuilderPanePicklist
          form={form}
          setForm={setForm}
          formErrors={formErrors}
          setFormErrors={setFormErrors}
        />
      )}
    </>
  );
};

const FieldBuilderPane = (): React.ReactElement => {
  return (
    <RightDrawerWrapper initialSize={FIELD_BUILDER_PANE_WIDTH}>
      <FieldBuilderPaneContent />
    </RightDrawerWrapper>
  );
};

export default FieldBuilderPane;
